<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
    <div class="container mx-auto py-8 px-4">
      <div class="max-w-md mx-auto">
        <!-- HSE Logo -->
        <div class="flex justify-center mb-6" style="margin-top: -20px; margin-bottom: 50px;">
          <img src="https://www.hse.ru/mirror/pubs/share/522217657" alt="HSE Logo" class="h-32">
        </div>
        
        <!-- Main Card -->
        <div class="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/10">
          <div class="space-y-8">
            <!-- Header -->
            <div class="text-center space-y-4">
              <h1 class="text-3xl font-bold text-white tracking-wider">Проверить позицию в очереди в приемной комиссии</h1>
              <p class="text-gray-400 leading-relaxed">
                Введите ваш номер в электронной очереди, который вы получили. После этого вы будете перенаправлены в телеграм-бота, где вы сможете удобно отслеживать позицию в очереди в режиме онлайн.
              </p>
            </div>

            <!-- Ticket Input Form -->
            <div class="space-y-6">
              <!-- Combined Input Group -->
              <div class="flex items-center bg-gray-900/50 rounded-xl p-2 shadow-inner border border-white/10">
                <!-- Letter Dropdown -->
                <select 
                  v-model="selectedLetter"
                  class="appearance-none bg-transparent text-white text-2xl font-mono w-16 text-center focus:outline-none"
                >
                  <option class="bg-gray-800" value="">-</option>
                  <option class="bg-gray-800" value="A">A</option>
                  <option class="bg-gray-800" value="M">M</option>
                  <option class="bg-gray-800" value="C">C</option>
                  <option class="bg-gray-800" value="S">S</option>
                </select>

                <div class="w-px h-8 bg-white/10 mx-2"></div>

                <!-- Number Inputs -->
                <div class="flex-1 flex gap-2">
                  <input
                    ref="digit1Input"
                    v-model="digit1"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="1"
                    class="w-full bg-transparent text-white text-2xl font-mono text-center focus:outline-none min-h-[3rem] sm:min-h-0"
                    placeholder="0"
                    @input="handleDigitInput($event, 1)"
                    @keydown="handleKeyDown($event, 1)"
                  />
                  <input
                    ref="digit2Input"
                    v-model="digit2"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="1"
                    class="w-full bg-transparent text-white text-2xl font-mono text-center focus:outline-none min-h-[3rem] sm:min-h-0"
                    placeholder="0"
                    @input="handleDigitInput($event, 2)"
                    @keydown="handleKeyDown($event, 2)"
                  />
                  <input
                    ref="digit3Input"
                    v-model="digit3"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="1"
                    class="w-full bg-transparent text-white text-2xl font-mono text-center focus:outline-none min-h-[3rem] sm:min-h-0"
                    placeholder="0"
                    @input="handleDigitInput($event, 3)"
                    @keydown="handleKeyDown($event, 3)"
                  />
                  <input
                    ref="digit4Input"
                    v-model="digit4"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="1"
                    class="w-full bg-transparent text-white text-2xl font-mono text-center focus:outline-none min-h-[3rem] sm:min-h-0"
                    placeholder="0"
                    @input="handleDigitInput($event, 4)"
                    @keydown="handleKeyDown($event, 4)"
                  />
                </div>
              </div>

              <!-- Submit Button -->
              <button 
                :disabled="!isFormValid"
                :class="[
                  'w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform active:scale-95',
                  isFormValid 
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg hover:shadow-blue-500/50' 
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                ]"
                @click="redirectToTelegram"
              >
                Отследить
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'

// Template refs for inputs
const digit1Input = ref<HTMLInputElement | null>(null)
const digit2Input = ref<HTMLInputElement | null>(null)
const digit3Input = ref<HTMLInputElement | null>(null)
const digit4Input = ref<HTMLInputElement | null>(null)

// Reactive data
const selectedLetter = ref('')
const digit1 = ref('')
const digit2 = ref('')
const digit3 = ref('')
const digit4 = ref('')

// Computed properties
const ticketNumber = computed(() => {
  if (selectedLetter.value && digit1.value && digit2.value && digit3.value && digit4.value) {
    return `${selectedLetter.value}${digit1.value}${digit2.value}${digit3.value}${digit4.value}`
  }
  return ''
})

const isFormValid = computed(() => {
  return selectedLetter.value && 
         digit1.value && 
         digit2.value && 
         digit3.value &&
         digit4.value &&
         /^\d$/.test(digit1.value) &&
         /^\d$/.test(digit2.value) &&
         /^\d$/.test(digit3.value) &&
         /^\d$/.test(digit4.value)
})

// Methods
const handleDigitInput = (event: Event, digitIndex: number) => {
  const input = event.target as HTMLInputElement
  const value = input.value
  
  if (value && !/^\d$/.test(value)) {
    input.value = ''
    if (digitIndex === 1) digit1.value = ''
    if (digitIndex === 2) digit2.value = ''
    if (digitIndex === 3) digit3.value = ''
    if (digitIndex === 4) digit4.value = ''
    return
  }
  
  if (value) {
    if (digitIndex === 1) {
      digit2Input.value?.focus()
    } else if (digitIndex === 2) {
      digit3Input.value?.focus()
    } else if (digitIndex === 3) {
      digit4Input.value?.focus()
    }
  }
}

const handleKeyDown = (event: KeyboardEvent, digitIndex: number) => {
  if (event.key === 'Backspace') {
    if (digitIndex === 2 && !digit2.value) {
      digit1Input.value?.focus()
    } else if (digitIndex === 3 && !digit3.value) {
      digit2Input.value?.focus()
    } else if (digitIndex === 4 && !digit4.value) {
      digit3Input.value?.focus()
    }
  }
}

const redirectToTelegram = () => {
  if (isFormValid.value) {
    const telegramUrl = `https://t.me/hseadmschedulebot?start=watch_${ticketNumber.value}`
    window.location.href = telegramUrl
  }
}
</script>

<style scoped>
/* Remove spinners from number inputs */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}

/* Custom select arrow */
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-down'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1em;
  padding-right: 2rem; /* Make space for the arrow */
}

/* Style for select options */
select option {
  background: #374151; /* gray-700 */
  color: white;
}
</style>